import type { FirebaseApp, FirebaseOptions } from 'firebase/app';
import { getApps, initializeApp } from 'firebase/app';
import type { Messaging } from 'firebase/messaging';
import { getMessaging, isSupported, onRegistered, register, unregister } from 'firebase/messaging';
import { apiDelete, apiPost } from './api';

const FIREBASE_APP_NAME = 'cabgo-web-push';
const REGISTERED_PUSH_TARGET_KEY = 'cabgo.push.token';
const REGISTRATION_TIMEOUT_MS = 10_000;

export type WebPushState =
  | 'unsupported'
  | 'unconfigured'
  | 'prompt'
  | 'registering'
  | 'enabled'
  | 'denied'
  | 'error';

function firebaseOptions(): FirebaseOptions | null {
  const raw = import.meta.env.VITE_FIREBASE_WEB_CONFIG_JSON;
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as FirebaseOptions;
    if (!parsed.apiKey || !parsed.projectId || !parsed.messagingSenderId || !parsed.appId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function firebaseMessaging(options: FirebaseOptions): Messaging {
  const existing = getApps().find((app) => app.name === FIREBASE_APP_NAME);
  const app: FirebaseApp = existing ?? initializeApp(options, FIREBASE_APP_NAME);
  return getMessaging(app);
}

function browserSupportsNotifications(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function supportsFirebaseMessaging(): Promise<boolean> {
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

export async function getWebPushState(): Promise<WebPushState> {
  if (!browserSupportsNotifications() || !(await supportsFirebaseMessaging())) return 'unsupported';
  if (!firebaseOptions() || !import.meta.env.VITE_FIREBASE_WEB_VAPID_PUBLIC_KEY) return 'unconfigured';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'granted' && localStorage.getItem(REGISTERED_PUSH_TARGET_KEY)) {
    return 'enabled';
  }
  return 'prompt';
}

async function registrationTarget(
  messaging: Messaging,
  serviceWorkerRegistration: ServiceWorkerRegistration,
) {
  return new Promise<string>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopListening = () => {};
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      stopListening();
      callback();
    };
    stopListening = onRegistered(messaging, (target) => finish(() => resolve(target)));
    timer = setTimeout(
      () => finish(() => reject(new Error('Timed out waiting for Firebase registration'))),
      REGISTRATION_TIMEOUT_MS,
    );
    void register(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_WEB_VAPID_PUBLIC_KEY,
      serviceWorkerRegistration,
    }).catch((error) => finish(() => reject(error)));
  });
}

async function syncPushTarget(target: string): Promise<void> {
  const previous = localStorage.getItem(REGISTERED_PUSH_TARGET_KEY);
  await apiPost('/devices', { platform: 'WEB', pushToken: target });
  localStorage.setItem(REGISTERED_PUSH_TARGET_KEY, target);
  if (previous && previous !== target) {
    await apiDelete('/devices', { pushToken: previous }).catch(() => {});
  }
}

/** Register the browser with FCM and sync its Firebase Installation ID to the API. */
export async function registerWebPush(requestPermission = false): Promise<WebPushState> {
  const current = await getWebPushState();
  if (current === 'unsupported' || current === 'unconfigured' || current === 'denied') return current;

  if (Notification.permission !== 'granted') {
    if (!requestPermission) return 'prompt';
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'prompt';
  }

  const options = firebaseOptions();
  if (!options) return 'unconfigured';

  try {
    const serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;
    const messaging = firebaseMessaging(options);
    const target = await registrationTarget(messaging, serviceWorkerRegistration);
    await syncPushTarget(target);
    return 'enabled';
  } catch {
    return 'error';
  }
}

export async function unregisterWebPush(): Promise<void> {
  const target = localStorage.getItem(REGISTERED_PUSH_TARGET_KEY);
  if (target) {
    await apiDelete('/devices', { pushToken: target }).catch(() => {});
  }
  try {
    const options = firebaseOptions();
    if (options && browserSupportsNotifications() && (await supportsFirebaseMessaging())) {
      await unregister(firebaseMessaging(options));
    }
  } catch {
    // Best effort cleanup on logout or when a user disables Web Push.
  } finally {
    localStorage.removeItem(REGISTERED_PUSH_TARGET_KEY);
  }
}
